import React, { forwardRef, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { GLOBAL_COMPONENTS_SOURCE_PAGE, trackComponentRenderError, trackOperation } from 'events/ops/opsEvent';
import type { UserOperation } from 'events/ops/opsEvent';
import { getType } from 'history/historyFactory';
import { logError } from 'middleware/logger';
import { recordToSplunk } from 'apis/recordToSplunk';

function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError' || /loading chunk/i.test(error.message);
}

interface Props {
  boundaryTag: string;
  children: React.ReactNode;
  isGlobalComponent?: boolean; // Marks this boundary as wrapping a global component (header, footer, banners)
  operationName?: UserOperation;
}

const MartyErrorBoundary = ({ boundaryTag, children, isGlobalComponent = false, operationName }: Props) => {
  // useCallback w/ react-hooks so we don't pass a brand new function every time this re-renders
  const FallbackComponent = useCallback(
    () => (
      <div
        data-test-id="errorBoundaryFallback"
        style={{ display: 'none' }}
        data-component-boundary-error={`Error while rendering the component ${boundaryTag ? `([${boundaryTag}])` : ''}`}
      />
    ),
    [boundaryTag]
  );

  const onError = useCallback(
    (error: Error) => {
      const tagPrefix = boundaryTag ? `[${boundaryTag}] ` : '';
      const operationFailurePrefix = operationName ? `${operationName} failure ` : '';
      const errorMessage = `An error has occurred while rendering component contained with MartyErrorBoundary: ${error.toString()}`;
      logError(`${tagPrefix}${operationFailurePrefix}${errorMessage}`);

      recordToSplunk(
        `type=componentRenderError&component=${encodeURIComponent(boundaryTag)}&error=${encodeURIComponent(error.toString())}&page=${encodeURIComponent(window.location.pathname)}`
      );

      // Chunk load failures are network issues, not component bugs — don't count them as render errors.
      if (isChunkLoadError(error)) {
        return;
      }

      const pageType = getType(window.location.pathname) || 'unknown';

      /**
       * sourcePage for render errors from global components (header, footer, banners).
       * These render on every page, so attributing their crashes to the current page inflates every page-specific alarm at once.
       * Emitting this value instead routes them to the shared GlobalComponents monitoring bucket, so one crash fires a single alarm.
       * Meanwhile the logs and Splunk events above keep the real page for drill-down.
       */
      const sourcePage = isGlobalComponent ? GLOBAL_COMPONENTS_SOURCE_PAGE : pageType;
      trackComponentRenderError(sourcePage, boundaryTag);

      if (operationName) {
        trackOperation(operationName, 'failure');
      }
    },
    [boundaryTag, isGlobalComponent, operationName]
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </ErrorBoundary>
  );
};

export const withErrorBoundary = <Props extends object>(
  boundaryTag: string,
  Component: React.ComponentType<Props>,
  operationName?: UserOperation,
  isGlobalComponent = false
) => {
  const WrappedComponent = forwardRef<React.ReactNode, Props>((props, ref) => (
    <MartyErrorBoundary boundaryTag={boundaryTag} isGlobalComponent={isGlobalComponent} operationName={operationName}>
      {/* @ts-expect-error TODO fix this */}
      <Component ref={ref} {...props} />
    </MartyErrorBoundary>
  ));
  WrappedComponent.displayName = `${getDisplayName(Component)}ErrorBoundary`;
  return WrappedComponent;
};
// helper for HOC
function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default MartyErrorBoundary;
